import { Link } from "react-router-dom";
import type { OcenaTuryDto, TreningDto } from "@shared/treningTypy.js";
import { IconOstrzezenie } from "../icons/index.js";
import { useNowyTrening } from "../../hooks/useTrening.js";
import {
  opisLiczbOceny,
  opisZaMaloDanychTreningu,
  plakietkaTury,
  plakietkaWyniku,
  wynikTury,
} from "../../lib/trening.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { DuzaPlakietkaOceny } from "./PlakietkaOceny.js";

/**
 * Wynik zakonczonego treningu: karta "Twoja ocena" (ocena slowna albo neutralne "Za mało danych"), "Następny krok" z
 * przyciskami oraz lista ocen kolejnych odpowiedzi. Dziala tak samo dla swiezo zakonczonego treningu i dla otwartego z
 * historii. Rozmowe (tylko do odczytu) dokleja strona pod spodem.
 *
 * Slowna ocena i liczby przy niej pochodza z serwera (`ocena.wynik`, liczone w kodzie z ocen tur, shared/ocenaSlowna.ts):
 * klient niczego tu nie zgaduje ani nie przelicza. Przy `za-malo-danych` nie ma slowa, jest powod. Fragmenty odpowiedzi w
 * ocenie tur sa tylko ZWERYFIKOWANE przez serwer (inaczej `fragment` jest null i cytatu nie ma).
 *
 * Kolory oceny (lib/trening.ts, trening.css): najlepsze zlote ("Bardzo dobrze", "Dobrze", "Zrobione"), srodek bez
 * oznaczenia ("Średnio", "Nie dotyczy"), do wymiany szare ("Słabo", "Do poprawy"). Zawsze ze slowem (ST-3).
 */
export function WidokOceny({
  trening,
  naglowekRef,
}: {
  trening: TreningDto;
  /** Naglowek karty oceny: po zakonczeniu przechodzi na niego fokus (nie zostaje na zniknietym przycisku). */
  naglowekRef?: React.Ref<HTMLHeadingElement>;
}): React.JSX.Element {
  const { zaloz, zajety, blad, zrodloNiewazne } = useNowyTrening();
  const ocena = trening.ocena;
  const wynik = ocena?.wynik ?? null;
  const tury = ocena ? [...ocena.tury].sort((a, b) => a.numer - b.numer) : [];

  /** Nowy trening z tym samym cwiczeniem, poziomem i opisem rozmowcy: parametr i numer cwiczenia niesie sam trening (TreningDto). */
  function cwiczJeszczeRaz(): void {
    void zaloz(() => ({
      lensSlug: trening.lensSlug,
      cwiczenieIndeks: trening.cwiczenieIndeks,
      poziom: trening.poziom,
      ...(trening.scenariusz ? { scenariusz: trening.scenariusz } : {}),
      ...(trening.zrodloAnalizyId && !zrodloNiewazne ? { zrodloAnalizyId: trening.zrodloAnalizyId } : {}),
    }));
  }

  return (
    <>
      <section className="card" aria-labelledby="trening-ocena-tytul">
        <h2 className="card-title" id="trening-ocena-tytul" tabIndex={-1} ref={naglowekRef}>
          Twoja ocena
        </h2>
        <div className="trening-ocena-wiersz">
          <DuzaPlakietkaOceny plakietka={plakietkaWyniku(wynik)} />
          <p className="trening-ocena-opis">
            {wynik === null
              ? "Dla tego ćwiczenia nie ma oceny."
              : wynik.rodzaj === "ocena"
                ? opisLiczbOceny(wynik)
                : opisZaMaloDanychTreningu(wynik)}
          </p>
        </div>
        {ocena && <p className="trening-podsumowanie">{ocena.podsumowanie}</p>}
        <p className="trening-stopka">
          Ocena słowna wynika z tego, w ilu odpowiedziach, w których ćwiczenie miało zastosowanie, udało Ci się je wykonać.
          To robocza reguła, do uzgodnienia z autorem metodyki.
        </p>
      </section>

      <Card tytul="Następny krok">
        <p className="trening-nastepny-krok">
          {ocena ? ocena.nastepnyKrok : "Spróbuj jeszcze raz albo wybierz inne ćwiczenie."}
        </p>
        <div className="trening-akcje">
          <Button wariant="primary" zajety={zajety} onClick={cwiczJeszczeRaz}>
            {zajety ? "Zakładam ćwiczenie..." : "Ćwicz jeszcze raz"}
          </Button>
          <Link className="btn btn--secondary" to="/cwiczenia">
            Wróć do ćwiczeń
          </Link>
        </div>
        {blad && (
          <p className="field-error" role="alert">
            <IconOstrzezenie size={14} />
            {blad.zdanie}
          </p>
        )}
      </Card>

      {tury.length > 0 && (
        <Card tytul="Ocena Twoich odpowiedzi">
          <ol className="rowlist">
            {tury.map((tura) => (
              <WierszTury key={tura.numer} tura={tura} />
            ))}
          </ol>
        </Card>
      )}
    </>
  );
}

function WierszTury({ tura }: { tura: OcenaTuryDto }): React.JSX.Element {
  const plakietka = plakietkaTury(tura);
  return (
    <li className="rowlist__row trening-tura" data-wynik={wynikTury(tura)}>
      <div className="trening-tura__naglowek">
        <span className="trening-tura__numer">Odpowiedź {tura.numer}</span>
        <Badge wariant={plakietka.wariant} kropka>
          {plakietka.etykieta}
        </Badge>
      </div>
      {tura.fragment && (
        <blockquote className="trening-cytat">
          <span className="tylko-dla-czytnika">Twój fragment: </span>
          {tura.fragment}
        </blockquote>
      )}
      <p className="trening-tura__komentarz">{tura.komentarz}</p>
    </li>
  );
}
